/* global SWDLocalise */
import { autocomplete } from '@algolia/autocomplete-js';
import algoliasearch from 'algoliasearch/lite';

const fieldStates = new WeakMap();
let activeField = null;
const OTHER_OPTION_ID = '__other__';

const PLACEHOLDER_COMPANIES = [
    { objectID: '1', name: 'Acme Corporation Ltd' },
    { objectID: '2', name: 'Barclays Bank PLC' },
    { objectID: '3', name: 'British Airways PLC' },
    { objectID: '4', name: 'Capita Group Ltd' },
    { objectID: '5', name: 'Deloitte LLP' },
    { objectID: '6', name: 'Enterprise Holdings Ltd' },
    { objectID: '7', name: 'Fidelity International Ltd' },
    { objectID: '8', name: 'GlaxoSmithKline PLC' },
    { objectID: '9', name: 'HSBC Holdings PLC' },
    { objectID: '10', name: 'International Business Machines UK Ltd' },
    { objectID: '11', name: 'J Sainsbury PLC' },
    { objectID: '12', name: 'Kingfisher PLC' },
    { objectID: '13', name: 'Lloyds Banking Group PLC' },
    { objectID: '14', name: 'Marks and Spencer Group PLC' },
    { objectID: '15', name: 'National Grid PLC' },
    { objectID: '16', name: 'Rolls-Royce Holdings PLC' },
    { objectID: '17', name: 'Tesco PLC' },
    { objectID: '18', name: 'Unilever PLC' },
    { objectID: '19', name: 'Vodafone Group PLC' },
    { objectID: '20', name: 'Workforce Assured Ltd' },
];

const hasAlgolia = () => Boolean(
    SWDLocalise?.algoliaAppID
    && SWDLocalise?.algoliaAPIKey,
);

const getItemLabel = (item, attribute) => {
    if (!item) return '';

    return String(item[attribute] ?? item.name ?? item.label ?? item.title ?? '');
};

const isOtherOption = (item) => item?.objectID === OTHER_OPTION_ID;

const createOtherOption = (label) => ({
    objectID: OTHER_OPTION_ID,
    name: label,
    isOtherOption: true,
});

const createManualItem = (value) => ({
    objectID: `manual-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    name: value,
    isManual: true,
});

const filterPlaceholderCompanies = (query, attribute) => {
    const normalisedQuery = query.trim().toLowerCase();

    if (!normalisedQuery) {
        return PLACEHOLDER_COMPANIES.slice(0, 8);
    }

    return PLACEHOLDER_COMPANIES.filter((item) => {
        const label = getItemLabel(item, attribute).toLowerCase();

        return label.includes(normalisedQuery);
    }).slice(0, 8);
};

const createSearchClient = () => algoliasearch(
    SWDLocalise.algoliaAppID,
    SWDLocalise.algoliaAPIKey,
);

const searchCompanies = async (query, indexName, attribute) => {
    if (!query.trim()) {
        return [];
    }

    if (!hasAlgolia()) {
        return filterPlaceholderCompanies(query, attribute);
    }

    const client = createSearchClient();
    const index = indexName || SWDLocalise.algoliaCompaniesIndex || 'companies';

    const { results } = await client.search([{
        indexName: index,
        params: {
            query,
            hitsPerPage: 8,
        },
    }]);

    return results[0]?.hits ?? [];
};

const syncHiddenValue = (field, selectedItems, attribute) => {
    const hiddenInput = field.querySelector('.js-form-search-select-value');
    const values = selectedItems.map((item) => getItemLabel(item, attribute));

    hiddenInput.value = values.join(' || ');
    hiddenInput.dispatchEvent(new Event('input', { bubbles: true }));
};

const SEARCH_INPUT_CLASSES = 'c-form-search-select__input group-[.has-error]/input:bg-error-light group-[.has-error]/input:border-error';

const renderTags = (field, selectedItems, attribute, isMulti, onRemove) => {
    if (!isMulti) return;

    const tagsContainer = field.querySelector('.js-form-search-select-tags');

    if (!tagsContainer) return;

    if (selectedItems.length === 0) {
        tagsContainer.innerHTML = '';
        tagsContainer.classList.add('hidden');
        return;
    }

    tagsContainer.classList.remove('hidden');
    tagsContainer.innerHTML = selectedItems.map((item, index) => {
        const label = getItemLabel(item, attribute);

        return `
            <button
                type="button"
                class="inline-flex items-center gap-1 py-2 px-4 bg-grey rounded-full text-xs font-medium js-form-search-select-tag"
                data-index="${index}"
                aria-label="Remove ${label}">
                <span aria-hidden="true">&times;</span>
                <span>${label}</span>
            </button>
        `;
    }).join('');

    tagsContainer.querySelectorAll('.js-form-search-select-tag').forEach((button) => {
        button.addEventListener('click', () => {
            onRemove(Number(button.dataset.index));
        });
    });
};

const syncTriggerInput = (state) => {
    const {
        container,
        placeholder,
        isMulti,
        attribute,
        selectedItems,
    } = state;
    let trigger = container.querySelector('.js-form-search-select-trigger');

    if (!trigger) {
        container.replaceChildren();
        trigger = document.createElement('input');
        trigger.type = 'text';
        trigger.readOnly = true;
        trigger.setAttribute('autocomplete', 'off');
        trigger.setAttribute('aria-autocomplete', 'list');
        container.appendChild(trigger);
    }

    const label = state.field.querySelector('label[for]');
    const searchInputId = label?.getAttribute('for') || '';

    trigger.className = `${SEARCH_INPUT_CLASSES} cursor-text js-form-search-select-trigger`;
    trigger.placeholder = placeholder;

    if (searchInputId) {
        trigger.id = searchInputId;
    }

    if (!isMulti && selectedItems.length) {
        trigger.value = getItemLabel(selectedItems[0], attribute);
    } else {
        trigger.value = '';
    }

    state.trigger = trigger;
};

const deactivateAutocomplete = (field) => {
    const state = fieldStates.get(field);

    if (!state?.instance) return;

    state.instance.destroy();
    state.instance = null;
    state.container?.replaceChildren();

    if (activeField === field) {
        activeField = null;
    }

    syncTriggerInput(state);
};

const activateAutocomplete = (field) => {
    const state = fieldStates.get(field);

    if (!state || state.instance) return;

    if (activeField && activeField !== field) {
        deactivateAutocomplete(activeField);
    }

    state.container.replaceChildren();

    const {
        container,
        placeholder,
        isMulti,
        allowOther,
        otherLabel,
        indexName,
        attribute,
        otherInput,
    } = state;

    state.instance = autocomplete({
        container,
        panelContainer: container,
        placeholder,
        openOnFocus: true,
        detachedMediaQuery: 'none',
        classNames: {
            root: 'c-form-search-select relative w-full',
            form: 'c-form-search-select__form relative',
            input: `${SEARCH_INPUT_CLASSES} js-form-search-select-input`,
            inputWrapper: 'c-form-search-select__input-wrapper relative',
            panel: 'c-form-search-select__panel',
            list: 'c-form-search-select__list',
            item: 'c-form-search-select__item',
        },
        render({ children, render: renderChildren }, root) {
            root.setAttribute('data-lenis-prevent', '');
            renderChildren(children, root);
        },
        getSources({ query }) {
            const sources = [{
                sourceId: 'companies',
                getItems() {
                    return searchCompanies(query, indexName, attribute);
                },
                getItemInputValue({ item }) {
                    return getItemLabel(item, attribute);
                },
                onSelect({ item, setQuery, setIsOpen }) {
                    if (isOtherOption(item)) {
                        state.selectOtherOption();
                        return;
                    }

                    if (state.isOtherMode && !isMulti) {
                        state.toggleOtherMode(false);

                        if (otherInput) {
                            otherInput.value = '';
                        }
                    }

                    if (isMulti) {
                        const label = getItemLabel(item, attribute);
                        const exists = state.selectedItems.some(
                            (selected) => getItemLabel(selected, attribute) === label,
                        );

                        if (!exists) {
                            state.setSelectedItems([...state.selectedItems, item]);
                        }

                        setQuery('');
                        setIsOpen(false);
                        return;
                    }

                    state.setSelectedItems([item]);
                    setQuery(getItemLabel(item, attribute));
                    setIsOpen(false);
                },
                templates: {
                    item({ item, html }) {
                        const label = getItemLabel(item, attribute);

                        return html`<div class="c-form-search-select__option">${label}</div>`;
                    },
                    noResults({ html }) {
                        return html`<div class="c-form-search-select__empty">No matching companies found.</div>`;
                    },
                },
            }];

            if (allowOther) {
                sources.push({
                    sourceId: 'other',
                    getItems() {
                        return [createOtherOption(otherLabel)];
                    },
                    getItemInputValue() {
                        return '';
                    },
                    onSelect() {
                        state.selectOtherOption();
                    },
                    templates: {
                        item({ item, html }) {
                            return html`<div class="c-form-search-select__option c-form-search-select__option--other">${getItemLabel(item, attribute)}</div>`;
                        },
                    },
                });
            }

            return sources;
        },
    });

    if (!isMulti && state.selectedItems.length) {
        state.instance.setQuery(getItemLabel(state.selectedItems[0], attribute));
    }

    activeField = field;
};

const openSearchSelect = (field) => {
    const state = fieldStates.get(field);

    if (!state || state.instance) return;

    activateAutocomplete(field);

    requestAnimationFrame(() => {
        const input = field.querySelector('.js-form-search-select-input');

        input?.focus();
        state.instance?.setIsOpen(true);
    });
};

const setupSearchSelectField = (field) => {
    if (fieldStates.has(field)) return;

    const container = field.querySelector('.js-form-search-select-autocomplete');

    if (!container) return;
    const placeholderEl = field.querySelector('.js-form-search-select-placeholder');
    const otherContainer = field.querySelector('.js-form-search-select-other');
    const otherInput = field.querySelector('.js-form-search-select-other-input');
    const otherAddButton = field.querySelector('.js-form-search-select-other-add');
    const otherBackButton = field.querySelector('.js-form-search-select-other-back');
    const isMulti = field.dataset.multi === '1';
    const allowOther = field.dataset.allowOther === '1';
    const otherLabel = field.dataset.otherLabel || 'Other';
    const indexName = field.dataset.index || '';
    const attribute = field.dataset.attribute || 'name';
    const placeholder = placeholderEl?.textContent?.trim() || 'Search company name…';
    const abortController = new AbortController();
    const { signal } = abortController;

    const state = {
        field,
        container,
        placeholder,
        isMulti,
        allowOther,
        otherLabel,
        indexName,
        attribute,
        otherInput,
        otherContainer,
        selectedItems: [],
        isOtherMode: false,
        instance: null,
    };

    state.setSelectedItems = (items) => {
        state.selectedItems = items;
        syncHiddenValue(field, state.selectedItems, attribute);
        renderTags(field, state.selectedItems, attribute, isMulti, (removeIndex) => {
            state.setSelectedItems(state.selectedItems.filter((_, index) => index !== removeIndex));
        });

        if (!isMulti && state.trigger) {
            state.trigger.value = state.selectedItems.length
                ? getItemLabel(state.selectedItems[0], attribute)
                : '';
        }
    };

    state.syncTrigger = () => {
        if (state.instance) return;

        if (!isMulti && state.isOtherMode) return;

        syncTriggerInput(state);
    };

    container.addEventListener('pointerdown', (event) => {
        if (!event.target.matches('.js-form-search-select-trigger')) return;

        openSearchSelect(field);
    }, { capture: true, signal });

    container.addEventListener('focusin', (event) => {
        if (!event.target.matches('.js-form-search-select-trigger')) return;

        openSearchSelect(field);
    }, { capture: true, signal });

    state.toggleOtherMode = (active) => {
        if (!allowOther || !otherContainer) return;

        state.isOtherMode = active;

        if (isMulti) {
            otherContainer.classList.toggle('hidden', !active);

            if (active) {
                otherInput?.focus();
            }

            return;
        }

        container.classList.toggle('hidden', active);
        otherContainer.classList.toggle('hidden', !active);

        if (active) {
            deactivateAutocomplete(field);
            otherInput?.focus();
            return;
        }

        if (otherInput) {
            otherInput.value = '';
        }

        state.syncTrigger();
    };

    state.addManualValue = () => {
        const value = otherInput?.value.trim() ?? '';

        if (!value) return;

        if (isMulti) {
            const normalisedValue = value.toLowerCase();
            const exists = state.selectedItems.some(
                (selected) => getItemLabel(selected, attribute).toLowerCase() === normalisedValue,
            );

            if (!exists) {
                state.setSelectedItems([...state.selectedItems, createManualItem(value)]);
            }

            otherInput.value = '';
            state.instance?.setQuery('');
            state.toggleOtherMode(false);
            return;
        }

        state.setSelectedItems([createManualItem(value)]);
    };

    state.selectOtherOption = () => {
        state.toggleOtherMode(true);
        state.instance?.setQuery('');
        state.instance?.setIsOpen(false);

        if (isMulti) {
            return;
        }

        state.setSelectedItems([]);
    };

    if (otherInput) {
        otherInput.addEventListener('input', () => {
            if (!state.isOtherMode || isMulti) return;

            const value = otherInput.value.trim();

            if (value) {
                state.setSelectedItems([createManualItem(value)]);
                return;
            }

            state.setSelectedItems([]);
        }, { signal });
    }

    otherAddButton?.addEventListener('click', state.addManualValue, { signal });

    otherInput?.addEventListener('keydown', (event) => {
        if (event.key !== 'Enter') return;

        event.preventDefault();
        state.addManualValue();
    }, { signal });

    otherBackButton?.addEventListener('click', () => {
        state.toggleOtherMode(false);
        state.setSelectedItems([]);
        state.syncTrigger();
    }, { signal });

    const controller = {
        reset() {
            state.setSelectedItems([]);

            if (otherInput) {
                otherInput.value = '';
            }

            state.toggleOtherMode(false);
            deactivateAutocomplete(field);
        },
    };

    state.abortController = abortController;
    state.controller = controller;
    fieldStates.set(field, state);
    state.syncTrigger();
};

export const destroySearchSelects = (root) => {
    root.querySelectorAll('.js-form-search-select').forEach((field) => {
        deactivateAutocomplete(field);

        const state = fieldStates.get(field);

        state?.abortController?.abort();
        fieldStates.delete(field);
    });
};

export const initSearchSelects = (root) => {
    root.querySelectorAll('.js-form-search-select').forEach((field) => {
        setupSearchSelectField(field);
    });
};

export const resetSearchSelects = (form) => {
    form.querySelectorAll('.js-form-search-select').forEach((field) => {
        fieldStates.get(field)?.controller?.reset();
    });
};

export const fillSearchSelectValue = (field, value) => {
    const state = fieldStates.get(field);

    if (!state || !value) return;

    const item = createManualItem(value);

    if (state.isMulti) {
        const exists = state.selectedItems.some(
            (selected) => getItemLabel(selected, state.attribute) === value,
        );

        if (!exists) {
            state.setSelectedItems([...state.selectedItems, item]);
        }

        return;
    }

    state.setSelectedItems([item]);
};
